import {
  AfterViewInit,
  DestroyRef,
  Directive,
  ElementRef,
  inject,
  Injector,
  NgZone,
  OnDestroy,
  Signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Streamable } from '@examdojo/core/typescript';
import { ensureObservable } from '@examdojo/rxjs';
import { ReactElement } from 'react';
import { createRoot, Root } from 'react-dom/client';
import { debounceTime, merge, tap } from 'rxjs';

@Directive()
export abstract class ReactWrapperComponent implements AfterViewInit, OnDestroy {
  private readonly ngZone = inject(NgZone);
  private readonly destroyRef = inject(DestroyRef);
  private readonly injector = inject(Injector);

  abstract readonly mountElementRef: Signal<ElementRef>;

  /**
   * Signals that should trigger re-renders of the React component.
   */
  protected abstract readonly reRenderTriggers: Array<Streamable<unknown>>;

  protected reactRoot?: Root;

  ngAfterViewInit(): void {
    const container = this.mountElementRef().nativeElement;
    this.reactRoot = createRoot(container);
    this.renderReact();

    this.reRenderOnChangeTriggered().pipe(takeUntilDestroyed(this.destroyRef)).subscribe();
  }

  ngOnDestroy(): void {
    if (this.reactRoot) {
      this.reactRoot.unmount();
      this.reactRoot = undefined;
    }
  }

  private reRenderOnChangeTriggered() {
    return merge(...this.reRenderTriggers.map((trigger) => ensureObservable(trigger, this.injector))).pipe(
      debounceTime(0),
      tap(() => {
        if (this.reactRoot) {
          this.renderReact();
        }
      }),
    );
  }

  /**
   * Provides the React element to render
   */
  protected abstract renderReactElement(): ReactElement | Promise<ReactElement>;

  protected async renderReact(): Promise<void> {
    return this.ngZone.runOutsideAngular(async () => {
      if (!this.reactRoot) {
        return;
      }

      const element = await this.renderReactElement();

      // Check again if root exists, as it might have been unmounted while awaiting
      if (!this.reactRoot) {
        return;
      }

      this.reactRoot.render(element);
    });
  }
}
